import {
    CoreFetch,
    createRequestFilters,
    ICustomFilter,
    ISelectOption,
    ListProviderPayerProvidersOrderEnum,
    ListProviderPayersOrderEnum,
    ListProviderProvidersOrderEnum,
    PayerProviderApi,
    PayerApi,
    ProviderPayerProvider,
    ProviderPayerProviderSortType,
    ProviderApi,
    ProviderPayerSortType,
    ProviderProviderSortType,
} from '@enter/core';
import { action, observable, runInAction } from 'mobx';
import _ from 'lodash';
import type { IFeatures } from './root.store';
import type { PayersGenerator } from './payers.generator';

export interface IPayersListingRequest {
    page?: number;
    pageSize?: number;
    filters?: ICustomFilter[];
    sort?: ProviderPayerProviderSortType[];
    order?: ListProviderPayerProvidersOrderEnum[];
}

export interface IPayersListing {
    payers: ProviderPayerProvider[];
    limit?: number;
    offset?: number;
    total?: number;
}

export class PayersStore {
    @observable public payers: ProviderPayerProvider[] = [];
    @observable public loading: boolean = false;
    @observable public total: number = 0;
    @observable public payerFilterOptions: ISelectOption[] = [];
    @observable public providerFilterOptions: ISelectOption[] = [];
    @observable public filterOptionsLoaded: boolean = false;

    private features?: IFeatures;
    private payersGenerator?: PayersGenerator;

    constructor(features?: IFeatures, payersGenerator?: PayersGenerator) {
        this.features = features;
        this.payersGenerator = payersGenerator;
    }

    @action
    public async loadPayersListing(request?: IPayersListingRequest): Promise<IPayersListing> {
        this.loading = true;
        try {
            if (this.features?.isDemo && this.payersGenerator) {
                const listing = this.payersGenerator.loadPayersListing(request);
                runInAction(() => {
                    this.payers = listing.payers;
                    this.total = listing.total ?? listing.payers.length;
                    this.loading = false;
                });
                return listing;
            }

            const limit = request?.pageSize ?? 25;
            const offset = request?.page && request?.pageSize ? request.page * request.pageSize : 0;
            const sort =
                request?.sort && request.sort.length > 0 ? request.sort : [ProviderPayerProviderSortType.CREATED];
            const order =
                request?.order && request.order.length > 0
                    ? request.order
                    : [ListProviderPayerProvidersOrderEnum.DESCENDING];
            const filters = request?.filters ? createRequestFilters(request.filters, true) : {};

            const raw = await CoreFetch<PayerProviderApi>(PayerProviderApi).listProviderPayerProvidersRaw({
                limit,
                offset,
                sort,
                order,
                ...filters,
            });

            const payers = await raw.value();
            const paginationTotal = raw.raw.headers.get('pagination-total');
            const total = paginationTotal ? parseInt(paginationTotal, 10) : payers.length;
            const resolvedTotal = Number.isNaN(total) ? payers.length : total;

            runInAction(() => {
                this.payers = payers;
                this.total = resolvedTotal;
                this.loading = false;
            });

            return {
                payers,
                limit,
                offset,
                total: resolvedTotal,
            };
        } catch (error) {
            runInAction(() => {
                this.loading = false;
            });
            throw error;
        }
    }

    @action
    public async loadFilterOptionsOnce(): Promise<void> {
        if (this.filterOptionsLoaded) {
            return;
        }

        if (this.features?.isDemo && this.payersGenerator) {
            const { payerOptions, providerOptions } = this.payersGenerator.loadFilterOptions();
            runInAction(() => {
                this.payerFilterOptions = payerOptions;
                this.providerFilterOptions = providerOptions;
                this.filterOptionsLoaded = true;
            });
            return;
        }

        const chunkLimit = 100;
        const maxPages = 50;

        const fetchAllPayers = async (): Promise<any[]> => {
            const all: any[] = [];
            for (let page = 0; page < maxPages; page++) {
                const offset = page * chunkLimit;
                const chunk = await CoreFetch<PayerApi>(PayerApi).listProviderPayers({
                    limit: chunkLimit,
                    offset,
                    sort: [ProviderPayerSortType.NAME],
                    order: [ListProviderPayersOrderEnum.ASCENDING],
                });
                all.push(...(chunk || []));
                if (!chunk || chunk.length < chunkLimit) {
                    break;
                }
            }
            return all;
        };

        const fetchAllProviders = async (): Promise<any[]> => {
            const all: any[] = [];
            for (let page = 0; page < maxPages; page++) {
                const offset = page * chunkLimit;
                const chunk = await CoreFetch<ProviderApi>(ProviderApi).listProviderProviders({
                    limit: chunkLimit,
                    offset,
                    sort: [ProviderProviderSortType.ORGANIZATION],
                    order: [ListProviderProvidersOrderEnum.ASCENDING],
                });
                all.push(...(chunk || []));
                if (!chunk || chunk.length < chunkLimit) {
                    break;
                }
            }
            return all;
        };

        const [payers, providers] = await Promise.all([fetchAllPayers(), fetchAllProviders()]);

        runInAction(() => {
            this.payerFilterOptions = _.chain(payers || [])
                .map(p => ({
                    label: p.name || '',
                    value: p.id || '',
                }))
                .filter(o => !!o.value)
                .uniqBy('value')
                .value();

            this.providerFilterOptions = _.chain(providers || [])
                .map(p => ({
                    label: p.organization || `${p.first || ''} ${p.last || ''}`.trim(),
                    value: p.id || '',
                }))
                .filter(o => !!o.value && !!o.label)
                .uniqBy('value')
                .value();

            this.filterOptionsLoaded = true;
        });
    }
}

